import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import logo from "../assets/images/logo1.png";
import {
  OWNER_MENUS,
  SUPPER_ADMIN_MENUS,
  KFT_OPERATION_USER_MENUS,
  OFF_TAKER_ADMIN_MENUS,
  OFF_TAKER_OPERATION_MENUS,
  AGRO_DEALER_ADMIN_MENUS,
  AGRO_DEALER_OPERATION_MENUS,
  AGRO_DEALER_STORE_MENUS,
  BANK_ADMIN_MENUS,
  INSURANCE_ADMIN_MENUS,
} from "../lib/menu-data";
import { MenuItem } from "../types/menu";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";
import { Icons } from "../assets/png";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useDisplayNameStore } from "../store/useDisplayNameStore";
import IconRenderer from "../util/icon-renderer";
import { getActiveMenuKeys } from "../util/get-active-menu-keys";

export const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout: signOut, user } = useAuth() as AuthContextValue;
  const { setDisplayName } = useDisplayNameStore();

  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [activeItem, setActiveItem] = useState("");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const activeKeys = getActiveMenuKeys(menus, pathname);

  useEffect(() => {
    setActiveItem(pathname);
  }, [pathname]);

  useEffect(() => {
    const openState: Record<string, boolean> = {};

    menus.forEach((menu) => {
      // Check if the parent menu matches the current pathname
      const parentMatch = pathname.startsWith(menu.href as string);
      // Check if any child matches
      const childMatch = menu.children?.some((child) =>
        pathname.startsWith(child.href as string)
      );

      if (parentMatch || childMatch) {
        openState[menu.key] = true; // open this parent menu
      }
    });

    setOpenMenus(openState);
  }, [pathname, menus]);

  useEffect(() => {
    // 1. Match parent menu
    const parent = menus.find((menu) =>
      pathname.startsWith(menu.href as string)
    );
    if (parent) {
      setDisplayName(parent.displayName as string);
      return;
    }

    // 2. Match child menu
    const allChildren = menus.flatMap((menu) => menu.children || []);
    const child = allChildren.find((c) =>
      pathname.startsWith(c.href as string)
    );
    if (child) {
      setDisplayName(child.displayName as string);
      return;
    }

    // fallback
    setDisplayName("");
  }, [pathname, menus, setDisplayName]);

  // Role-based menu assignment
  useEffect(() => {
    if (user && user.roleName) {
      if (user.roleName === "ROLE_SUPER_ADMIN") {
        setMenus(OWNER_MENUS);
      } else if (user.roleName === "ROLE_KFT_SUPER_ADMIN") {
        setMenus(SUPPER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_KFT_SP_ADMIN") {
        setMenus(KFT_OPERATION_USER_MENUS);
      } else if (user.roleName === "ROLE_OFF_TAKER_ADMIN") {
        setMenus(OFF_TAKER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_OFF_TAKER_OPERATION") {
        setMenus(OFF_TAKER_OPERATION_MENUS);
      } else if (user.roleName === "ROLE_MPC_ADMIN") {
        setMenus(AGRO_DEALER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_MPC_OPERATION") {
        setMenus(AGRO_DEALER_OPERATION_MENUS);
      } else if (user.roleName === "MOB_ROLE_AGRO_DEALER") {
        setMenus(AGRO_DEALER_STORE_MENUS);
      } else if (user.roleName === "ROLE_MFI_ADMIN") {
        setMenus(BANK_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_MFI_OPERATION") {
        setMenus(BANK_ADMIN_MENUS); // Note: This uses BANK_ADMIN_MENUS, should it be BANK_OPERATION_MENUS?
      } else if (user.roleName === "INSURANCE_ADMIN") {
        setMenus(INSURANCE_ADMIN_MENUS);
      } else if (user.roleName === "AGRO_DEALER_ADMIN") {
        setMenus(AGRO_DEALER_ADMIN_MENUS);
      }
    }
  }, [user]);

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({
      [key]: !prev[key], // close others
    }));
  };

  const handleNavigation = (item?: MenuItem) => {
    if (item?.href as string) {
      setActiveItem(item?.href as string);
      navigate(item?.href as string);
    }
  };

  const handleLogout = () => {
    const token = localStorage.getItem("token");

    if (token) {
      signOut();
      localStorage.removeItem("token");
      navigate("/");
    }
  };

  // Helper function to render menu items recursively
  const renderMenuItems = (menuItems: MenuItem[]) => {
    return menuItems.map((item) => {
      const isDisabled = user?.status !== 1;

      if (item.type === "divider") {
        return (
          <div key={item.key} className="border-t border-green-700 my-2" />
        );
      }

      if (item.children) {
        // Menu with children (collapsible)
        return (
          <div key={item.key}>
            <button
              disabled={isDisabled}
              onClick={() => !isDisabled && toggleMenu(item.key)}
              className={`flex items-center w-11/12 justify-between px-2 py-2 ${
                isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }
              ${
                activeItem.includes(item.key?.toLowerCase()) ||
                activeItem.includes(item.label?.toLowerCase())
                  ? "text-[#AACC54] bg-[#295C56] rounded-xl w-11/12 border-l-4 border-l-[#AACC54]"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2 ">
                <IconRenderer icon={item.icon} size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>

              <span className="text-white">
                {item.children && openMenus[item.key] ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </span>
            </button>

            {item.children && openMenus[item.key] && (
              <div className="ml-6 mt-1 space-y-1">
                {renderMenuItems(item.children)}
              </div>
            )}
          </div>
        );
      }

      // Single menu item
      return (
        <SidebarItem
          key={item.key}
          icon={item.icon}
          isActive={activeKeys.includes(item.key)}
          onClick={() => handleNavigation(item)}
          isChild={item.isChild}
          disabled={isDisabled}
        >
          {item.label}
        </SidebarItem>
      );
    });
  };

  return (
    <div className="w-60 rounded-2xl text-white flex flex-col h-auto">
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-5">
        <img
          src={logo}
          width={136}
          height={39}
          alt="ShegaFre"
          className="w-[136px] h-10"
        />
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-2 space-y-1">
        {menus.length > 0 ? (
          renderMenuItems(menus)
        ) : (
          <div className="text-center text-green-300 py-4">
            Loading menus...
          </div>
        )}

        {/* Logout - always visible */}
        <SidebarItem icon={Icons.logoutIcon} onClick={handleLogout}>
          Logout
        </SidebarItem>
      </nav>
    </div>
  );
};
